import { computed } from 'vue';
import { useRoute, useRouter, type RouteLocationRaw } from 'vue-router';

import { DEFAULT_LOCALE, type Locale } from '@/i18n/locales';
import { PAGES, routeName, slugFor } from '@/router/pages';

/**
 * Locale-aware link helpers.
 *
 * Replaces Blade's `route('about')` (which resolved a translated slug for the
 * active locale) and `LaravelLocalization::getLocalizedURL()` for the language
 * switcher.
 */
export function useLocalePath() {
  const route = useRoute();
  const router = useRouter();

  const locale = computed<Locale>(() => route.meta.locale ?? DEFAULT_LOCALE);

  /** Route target for a page key in the current (or given) locale. */
  function localeRoute(pageKey: string, forLocale?: Locale): RouteLocationRaw {
    return { name: routeName(pageKey, forLocale ?? locale.value) };
  }

  /** Resolved href for a page key, for use in plain anchors. */
  function localePath(pageKey: string, forLocale?: Locale): string {
    return router.resolve(localeRoute(pageKey, forLocale)).href;
  }

  /**
   * The current page's URL in another locale, preserving hash and query.
   * Falls back to that locale's home page when the page has no counterpart.
   */
  function switchLocalePath(target: Locale): string {
    const pageKey = route.meta.pageKey;
    const page = PAGES.find((candidate) => candidate.key === pageKey);
    const name = routeName(page ? page.key : 'home', target);

    return router.resolve({
      name,
      query: route.query,
      hash: route.hash,
    }).href;
  }

  /** True when `pageKey` is the page currently being displayed. */
  function isActive(pageKey: string): boolean {
    return route.meta.pageKey === pageKey;
  }

  return {
    locale,
    localeRoute,
    localePath,
    switchLocalePath,
    isActive,
    slugFor,
  };
}
